import {FC} from 'react';

import {UnreachableError} from '../../../libs/shared/core/src/helpers/types/customErrors';
import {PushConfigModel, PushConnectorsEnum} from '../../../libs/shared/core/src/models/api/pushConfigModel';
import {buildAblyPresenceCompanyChannelName} from '../../../libs/shared/core/src/services/push/ably/ablyChannelNameHelpers';
import {
  PushPresenceMessage,
  PushPresenceMessageTypesEnum,
} from '../../../libs/shared/core/src/services/push/common/pushMessage';
import {buildOnlineTeammate} from '../../../libs/shared/core/src/services/push/common/pushMessageHelpers';
import {getPushConfig} from '../../../libs/shared/core/src/store/data/config/configSelectors';
import {usePushControllerContext} from '../../../libs/shared/ui/src/push/pushControllerContext';
import {usePushV2PresenceEvent} from '../../../libs/shared/ui/src/push/usePushEvents';
import {useAppDispatch, useAppSelector} from '../../../src/helpers/appReduxHelpers';
import {getCompany} from '../../../src/store/data/company/companySelectors';
import {
  setOnlineTeammatesAction,
  updateIsTeammateOnlineAction,
} from '../../../src/store/data/teammates/onlineTeammatesActions';

export const PushPresenceEvents: FC = () => {
  const {pushController} = usePushControllerContext();
  const pushConfig = useAppSelector(getPushConfig);
  const company = useAppSelector(getCompany);
  const dispatch = useAppDispatch();

  usePushV2PresenceEvent((event) => {
    if (!isCompanyPresenceEvent(pushConfig, event, company.id)) {
      return;
    }

    switch (event.type) {
      case PushPresenceMessageTypesEnum.INIT:
        pushController.enqueueTask(() => () => {
          dispatch(setOnlineTeammatesAction(event.members.map(buildOnlineTeammate)));
        });
        break;
      case PushPresenceMessageTypesEnum.ENTER:
        pushController.enqueueTask(() => () => {
          dispatch(updateIsTeammateOnlineAction(buildOnlineTeammate(event.member), true));
        });
        break;
      case PushPresenceMessageTypesEnum.LEAVE:
        pushController.enqueueTask(() => () => {
          dispatch(updateIsTeammateOnlineAction(buildOnlineTeammate(event.member), false));
        });
        break;
      default:
      // no-op
    }
  });

  return null;
};

function isCompanyPresenceEvent(pushConfig: PushConfigModel, event: PushPresenceMessage, companyId: number) {
  const {connector} = pushConfig;
  switch (connector) {
    case PushConnectorsEnum.ABLY:
      return event.channel === buildAblyPresenceCompanyChannelName(companyId, pushConfig.roomsPrefix);
    case PushConnectorsEnum.SOCKET_IO:
      // Company presence is not implemented for Socket.IO
      return false;
    default:
      throw new UnreachableError(connector);
  }
}
